import React, { useMemo } from 'react';
import cn from 'classnames';
import _get from 'lodash/get';
import _size from 'lodash/size';
import styles from './index.module.scss';

import { UiKit } from 'components';
import { StepperContext } from './Provider';
import Node from './Node';

/**
 * @param {Object} props
 * @param {import('utils/types').Mode} [props.mode]
 * @param {import('utils/types').StepperVariant} [props.variant]
 * @param {import('utils/types').Orientation} [props.orientation]
 * @param {import('utils/types').StepperStep['id']} [props.value]
 * @param {import('utils/types').StepperStep[]} [props.steps]
 * @param {string} [props.className]
 */
export default function Stepper({
  mode = 'light',
  variant = 'default',
  orientation = 'vertical',
  value = null,
  steps = [],
  className = ''
}) {
  const direction = orientation === 'vertical' ? 'column' : 'row';
  const flippedDirection = orientation === 'vertical' ? 'row' : 'column';
  const nodeGap = orientation === 'vertical' ? 4 : 8;

  const { allStepsPositionMap, stepsPositionMap, lastStepId } = useMemo(() => {
    return steps.reduce(
      (acc, step, index) => {
        acc.allStepsPositionMap[step.id] = index + 1;
        if (!step.isHidden) {
          acc.stepsPositionMap[step.id] = _size(acc.stepsPositionMap) + 1;
          acc.lastStepId = step.id;
        }
        return acc;
      },
      { allStepsPositionMap: {}, stepsPositionMap: {}, lastStepId: null }
    );
  }, [steps]);

  const stepsRender = steps.map((step, index) => {
    const stepNumber = index + 1;

    const activeStepNumber = _get(allStepsPositionMap, value, Infinity);
    const isCompleted = activeStepNumber > stepNumber;
    const isActive = activeStepNumber === stepNumber;
    const isPending = activeStepNumber < stepNumber;

    if (step.isHidden) return null;

    return (
      <UiKit.Flex
        key={step.id}
        direction={flippedDirection}
        gap={8}
        className={cn(styles.step, {
          [styles.active]: isActive,
          [styles.completed]: isCompleted,
          [styles.pending]: isPending
        })}
      >
        <Node id={step.id} />
        <UiKit.Flex direction="column" className={styles.info}>
          <p className={styles.title}>{step.title}</p>
          <span className={styles.description}>{step.description}</span>
        </UiKit.Flex>
      </UiKit.Flex>
    );
  });

  return (
    <StepperContext.Provider
      value={{
        mode,
        variant,
        nodeGap,
        direction,
        orientation,
        value,
        lastStepId,
        stepsPositionMap
      }}
    >
      <div
        data-mode={mode}
        data-variant={variant}
        data-orientation={orientation}
        className={cn(styles.container, className)}
      >
        <UiKit.Flex direction={direction} gap={nodeGap} justify="center">
          {stepsRender}
        </UiKit.Flex>
      </div>
    </StepperContext.Provider>
  );
}
